import React from 'react';
import {
  Button,
  Checkbox,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';
import {
  OrderPageData,
  OrderPageResourceMaskData,
  ResourceCalculationMode,
  ResourceOptionData,
  SchedulerAreaData,
} from '../../api';
import { localizedLabel, masksIntersect, maskToIndexes, unionMasks } from '../../utils/maskUtils';

type PageMasksEditorProps = {
  allowedMasks: OrderPageResourceMaskData[];
  areas: SchedulerAreaData[];
  resourceCount: number;
  orderPages: OrderPageData[];
  /** Pages already claimed by another config — the backend rejects a page in two configs. */
  pagesUsedElsewhere: Set<string>;
  languages: string[];
  language: string;
  disabled: boolean;
  onChange: (allowedMasks: OrderPageResourceMaskData[]) => void;
};

/**
 * A page's mask is composed by picking AREAS, never raw bits: the admin thinks "this page sells
 * the yard", not "bits 23 to 33". A mask that does not line up with whole areas is left alone and
 * shown as a resource list.
 */
export const PageMasksEditor = ({
  allowedMasks,
  areas,
  resourceCount,
  orderPages,
  pagesUsedElsewhere,
  languages,
  language,
  disabled,
  onChange,
}: PageMasksEditorProps) => {
  const { t } = useTranslation();

  const update = (index: number, patch: Partial<OrderPageResourceMaskData>) =>
    onChange(allowedMasks.map((mask, position) => (position === index ? { ...mask, ...patch } : mask)));

  const updateOption = (maskIndex: number, optionIndex: number, patch: Partial<ResourceOptionData>) =>
    update(maskIndex, {
      resource_options: (allowedMasks[maskIndex].resource_options ?? []).map((option, position) =>
        position === optionIndex ? { ...option, ...patch } : option,
      ),
    });

  const selectedAreaIds = (mask: string) => areas.filter((area) => masksIntersect(area.mask, mask)).map((a) => a.id);

  const maskFromAreaIds = (areaIds: string[]) =>
    areas
      .filter((area) => areaIds.includes(area.id))
      .reduce((mask, area) => unionMasks(mask, area.mask), '0'.repeat(resourceCount))
      .slice(-resourceCount);

  const availablePages = orderPages.filter(
    (page) => !pagesUsedElsewhere.has(page.id) && !allowedMasks.some((mask) => mask.order_page_id === page.id),
  );

  /** A disabled "add page" control should say which of the reasons applies. */
  const claimedElsewhere = orderPages.filter((page) => pagesUsedElsewhere.has(page.id));
  const unavailableReason = (() => {
    if (availablePages.length > 0) {
      return undefined;
    }
    if (orderPages.length === 0) {
      return t('sharedResources.schedulerConfigs.noOrderPages');
    }
    if (claimedElsewhere.length > 0) {
      return t('sharedResources.schedulerConfigs.pagesClaimedElsewhere', {
        pages: claimedElsewhere.map((page) => localizedLabel(page.label, language, page.id)).join(', '),
      });
    }
    return t('sharedResources.schedulerConfigs.allPagesAdded');
  })();

  return (
    <Grid container flexDirection="column" wrap="nowrap" gap={1}>
      <Typography variant="subtitle2">{t('sharedResources.schedulerConfigs.pages')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('sharedResources.schedulerConfigs.pagesHelp')}
      </Typography>

      {allowedMasks.map((allowedMask, index) => {
        const page = orderPages.find((candidate) => candidate.id === allowedMask.order_page_id);
        const areaIds = selectedAreaIds(allowedMask.mask);
        const isStatic = allowedMask.resource_calculation_mode === ResourceCalculationMode.STATIC;

        return (
          <Grid key={allowedMask.order_page_id} container flexDirection="column" wrap="nowrap" gap={0.5}>
            <Grid container alignItems="center" gap={1} wrap="nowrap">
              <Typography sx={{ width: 160, fontSize: '13px' }}>
                {page ? localizedLabel(page.label, language, page.id) : allowedMask.order_page_id}
              </Typography>

              {areas.length > 0 ? (
                <Select
                  multiple
                  size="small"
                  sx={{ minWidth: 200 }}
                  value={areaIds}
                  disabled={disabled}
                  displayEmpty
                  renderValue={(selected) =>
                    selected.length === 0
                      ? t('sharedResources.schedulerConfigs.noAreasSelected')
                      : areas
                          .filter((area) => selected.includes(area.id))
                          .map((area) => localizedLabel(area.label, language, area.id))
                          .join(', ')
                  }
                  onChange={(event) => {
                    const ids = event.target.value as string[];
                    update(index, { mask: maskFromAreaIds(ids) });
                  }}>
                  {areas.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      <Checkbox size="small" checked={areaIds.includes(area.id)} />
                      <ListItemText primary={localizedLabel(area.label, language, area.id)} />
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Tooltip title={t('sharedResources.schedulerConfigs.noAreasHelp')}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200 }}>
                    {t('sharedResources.schedulerConfigs.allResources', {
                      count: maskToIndexes(allowedMask.mask).length,
                    })}
                  </Typography>
                </Tooltip>
              )}

              <Select
                size="small"
                sx={{ width: 150 }}
                value={allowedMask.resource_calculation_mode ?? ResourceCalculationMode.PERSONS}
                disabled={disabled}
                onChange={(event) => {
                  const mode = event.target.value as ResourceCalculationMode;
                  update(index, {
                    resource_calculation_mode: mode,
                    static_resources:
                      mode === ResourceCalculationMode.STATIC ? allowedMask.static_resources ?? 1 : undefined,
                  });
                }}>
                {Object.values(ResourceCalculationMode).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {t(`sharedResources.schedulerConfigs.mode_${mode}`)}
                  </MenuItem>
                ))}
              </Select>

              {isStatic && (
                <TextField
                  size="small"
                  type="number"
                  sx={{ width: 110 }}
                  label={t('sharedResources.schedulerConfigs.staticResources')}
                  value={allowedMask.static_resources ?? 1}
                  disabled={disabled}
                  inputProps={{ min: 1, max: resourceCount, dir: 'ltr' }}
                  onChange={(event) => update(index, { static_resources: Number(event.target.value) })}
                  error={!allowedMask.static_resources}
                />
              )}

              <IconButton disabled={disabled} onClick={() => onChange(allowedMasks.filter((unused, i) => i !== index))}>
                <Delete />
              </IconButton>
            </Grid>

            {areas.length > 0 && (
              <Grid container flexDirection="column" wrap="nowrap" gap={1} sx={{ paddingInlineStart: 4 }}>
                {(allowedMask.resource_options ?? []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('sharedResources.schedulerConfigs.resourceOptions')}
                  </Typography>
                )}
                {(allowedMask.resource_options ?? []).map((resourceOption, optionIndex) => (
                  <Grid key={resourceOption.id} container flexDirection="column" wrap="nowrap" gap={0.5} sx={{ mb: 1 }}>
                    <Grid container alignItems="center" gap={1} wrap="nowrap">
                      {languages.map((lang) => (
                        <TextField
                          key={lang}
                          size="small"
                          sx={{ width: 140 }}
                          label={t(`emailTemplates.${lang}`)}
                          value={(resourceOption.label as Record<string, string | undefined>)?.[lang] ?? ''}
                          disabled={disabled}
                          onChange={(event) =>
                            updateOption(index, optionIndex, {
                              label: { ...resourceOption.label, [lang]: event.target.value },
                            })
                          }
                        />
                      ))}

                      <Select
                        multiple
                        size="small"
                        sx={{ minWidth: 200 }}
                        value={selectedAreaIds(resourceOption.mask)}
                        disabled={disabled}
                        displayEmpty
                        renderValue={(selected) =>
                          selected.length === 0
                            ? t('sharedResources.schedulerConfigs.noAreasSelected')
                            : areas
                                .filter((area) => selected.includes(area.id))
                                .map((area) => localizedLabel(area.label, language, area.id))
                                .join(', ')
                        }
                        onChange={(event) => {
                          const ids = event.target.value as string[];
                          updateOption(index, optionIndex, { mask: maskFromAreaIds(ids) });
                        }}>
                        {areas.map((area) => (
                          <MenuItem key={area.id} value={area.id}>
                            <Checkbox size="small" checked={selectedAreaIds(resourceOption.mask).includes(area.id)} />
                            <ListItemText primary={localizedLabel(area.label, language, area.id)} />
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        disabled={disabled}
                        onClick={() =>
                          update(index, {
                            resource_options: (allowedMask.resource_options ?? []).filter(
                              (unused, position) => position !== optionIndex,
                            ),
                          })
                        }>
                        <Delete />
                      </IconButton>
                    </Grid>

                    <Grid container alignItems="center" gap={1} wrap="nowrap">
                      {languages.map((lang) => (
                        <TextField
                          key={lang}
                          size="small"
                          sx={{ width: 288 }}
                          label={`${t('sharedResources.schedulerConfigs.optionDescription')} · ${t(
                            `emailTemplates.${lang}`,
                          )}`}
                          value={(resourceOption.description as Record<string, string | undefined>)?.[lang] ?? ''}
                          disabled={disabled}
                          onChange={(event) => {
                            const description = { ...resourceOption.description, [lang]: event.target.value };
                            const hasText = Object.values(description).some((value) => value?.trim());
                            updateOption(index, optionIndex, { description: hasText ? description : undefined });
                          }}
                        />
                      ))}
                    </Grid>
                  </Grid>
                ))}
                <Grid container>
                  <Tooltip title={t('sharedResources.schedulerConfigs.resourceOptionsHelp')}>
                    <span>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        disabled={disabled}
                        onClick={() =>
                          update(index, {
                            resource_options: [
                              ...(allowedMask.resource_options ?? []),
                              { id: uuid(), label: {}, mask: '0'.repeat(resourceCount) },
                            ],
                          })
                        }>
                        {t('sharedResources.schedulerConfigs.addResourceOption')}
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            )}
          </Grid>
        );
      })}

      <Grid container alignItems="center" gap={1} wrap="nowrap">
        <Tooltip title={unavailableReason ?? ''}>
          <span>
            <Select
              size="small"
              value=""
              displayEmpty
              disabled={disabled || availablePages.length === 0}
              sx={{ minWidth: 220 }}
              renderValue={() => (
                <Grid container alignItems="center" gap={0.5}>
                  <Add fontSize="small" />
                  {t('sharedResources.schedulerConfigs.addPage')}
                </Grid>
              )}
              onChange={(event) =>
                onChange([...allowedMasks, { order_page_id: event.target.value, mask: '0'.repeat(resourceCount) }])
              }>
              {availablePages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {localizedLabel(page.label, language, page.id)}
                </MenuItem>
              ))}
            </Select>
          </span>
        </Tooltip>
        {unavailableReason && (
          <Typography variant="caption" color="text.secondary">
            {unavailableReason}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
